import { useI18n } from '@affine/i18n';
import { UploadIcon } from '@blocksuite/icons/rc';
import { useEffect, useRef, useState } from 'react';

import { Button } from '../../../button';
import { type IconData, IconType } from '../../type';
import { getIconBlobSource, type IconBlobEntry } from './blob-source';
import * as styles from './custom-icon-picker.css';
import {
  ALLOWED_TYPES,
  DIMENSIONS_ERROR,
  type IconFileError,
  resizeImage,
  validateIconFile,
} from './image';

interface CustomIconPickerProps {
  onSelect: (data: Blob | IconData) => void;
}

const IMAGE_MIME = /^image\/(png|jpe?g|webp|gif|svg\+xml)$/;
const MAX_EXISTING = 120;

/** Images that are already stored in the workspace, newest first. */
const ExistingImages = ({
  onSelect,
}: {
  onSelect: (data: IconData) => void;
}) => {
  const t = useI18n();
  const source = getIconBlobSource();
  const [entries, setEntries] = useState<IconBlobEntry[] | null>(null);

  useEffect(() => {
    if (!source) return;
    let cancelled = false;
    source
      .list()
      .then(list => {
        if (cancelled) return;
        setEntries(
          list
            .filter(e => IMAGE_MIME.test(e.mime ?? ''))
            .sort(
              (a, b) =>
                (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)
            )
            .slice(0, MAX_EXISTING)
        );
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  if (!source || !entries || entries.length === 0) return null;

  return (
    <div className={styles.existing}>
      <span className={styles.hint}>
        {t['com.affine.iconPicker.custom.existing']()}
      </span>
      <div className={styles.existingGrid}>
        {entries.map(entry => (
          <ExistingImage
            key={entry.key}
            entry={entry}
            getUrl={source.getUrl}
            onSelect={() =>
              onSelect({ type: IconType.Blob, blobId: entry.key })
            }
          />
        ))}
      </div>
    </div>
  );
};

const ExistingImage = ({
  entry,
  getUrl,
  onSelect,
}: {
  entry: IconBlobEntry;
  getUrl: (key: string) => Promise<string | undefined>;
  onSelect: () => void;
}) => {
  const [url, setUrl] = useState<string | undefined>();
  useEffect(() => {
    let cancelled = false;
    getUrl(entry.key)
      .then(u => {
        if (!cancelled) setUrl(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [entry.key, getUrl]);

  return (
    <button type="button" className={styles.existingItem} onClick={onSelect}>
      {url ? <img src={url} alt="" className={styles.existingImage} /> : null}
    </button>
  );
};

export const CustomIconPicker = ({ onSelect }: CustomIconPickerProps) => {
  const t = useI18n();
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  // The file dialog can deliver a file while an older resize task still
  // runs, so each task gets an id and only the latest task may apply.
  const requestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  const handleFile = (file: File) => {
    const requestId = ++requestIdRef.current;
    setError(null);

    const validationError = validateIconFile(file);
    if (validationError) {
      const messages: Record<IconFileError, string> = {
        'unsupported-type':
          t['com.affine.iconPicker.custom.error.unsupported'](),
        'too-large': t['com.affine.iconPicker.custom.error.tooLarge'](),
        'svg-too-large': t['com.affine.iconPicker.custom.error.svgTooLarge'](),
      };
      setError(messages[validationError]);
      // This call also cancelled any older task, so its `finally` will not
      // clear the processing state.
      setProcessing(false);
      return;
    }

    setProcessing(true);
    resizeImage(file)
      .then(blob => {
        if (requestId !== requestIdRef.current) return;
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        setPreview(url);
        onSelect(blob);
      })
      .catch((error: unknown) => {
        if (requestId !== requestIdRef.current) return;
        setError(
          error instanceof Error && error.message === DIMENSIONS_ERROR
            ? t['com.affine.iconPicker.custom.error.dimensions']()
            : t['com.affine.iconPicker.custom.error.failed']()
        );
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setProcessing(false);
      });
  };

  const clearPreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const openFileDialog = () => {
    if (!processing) inputRef.current?.click();
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.uploadZone}
        onClick={openFileDialog}
        role="button"
        tabIndex={0}
        aria-label={t['com.affine.iconPicker.custom.upload']()}
        aria-busy={processing}
        data-dragging={dragging ? '' : undefined}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFileDialog();
          }
        }}
        onDragOver={e => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault();
          setDragging(false);
          if (processing) return;
          const file = e.dataTransfer.files[0];
          if (file) handleFile(file);
        }}
      >
        {processing ? (
          <span>{t['com.affine.iconPicker.custom.processing']()}</span>
        ) : preview ? (
          <img src={preview} alt="" className={styles.preview} />
        ) : (
          <>
            <UploadIcon fontSize={24} />
            <span>{t['com.affine.iconPicker.custom.upload']()}</span>
            <span className={styles.hint}>
              {t['com.affine.iconPicker.custom.hint']()}
            </span>
          </>
        )}
      </div>

      {preview && !processing && (
        <Button variant="plain" onClick={clearPreview}>
          {t['com.affine.iconPicker.custom.chooseAnother']()}
        </Button>
      )}

      {error && <span className={styles.error}>{error}</span>}

      <ExistingImages onSelect={onSelect} />

      <input
        ref={inputRef}
        type="file"
        accept={[...ALLOWED_TYPES].join(',')}
        className={styles.fileInput}
        onChange={e => {
          const file = e.target.files?.[0];
          // Reset the input, so a user can pick the same file again.
          e.target.value = '';
          if (file) handleFile(file);
        }}
      />
    </div>
  );
};
